import { Duration } from 'dayjs/plugin/duration.js';
import { Currency } from '../../models/serializable/currency.js';
import { Price } from '../../models/serializable/price.js';
import { VersionRange } from '../../models/core/version-range.js';
import { cloneMap, convertMapEntries, DataModel,
  DataModelBuilder, isSameData, isSameMap,
  mapToData, SerializationContext, SerializedData, toData } from '../../models/dto/dto.js';
import { DurationRange } from '../../models/serializable/duration-range.js';
import { LocalizedString } from '../../models/serializable/localized-string.js';
import { PriceRange } from '../../models/serializable/price-range.js';
import { PriceType } from '../../models/serializable/price-type.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Language } from '../../models/serializable/language.js';

export class ServiceModifier extends DataModel {
  static readonly TYPE_ID: string = 'cb74e641f64a49d3b096eba21e772c89';
  static readonly SCHEMA_VERSION: number = 6;

  private constructor(
    public readonly names: LocalizedString,
    public readonly descriptions: LocalizedString,
    public readonly isPricePublic: boolean,
    public readonly priceType: PriceType,
    public readonly priceRanges: Map<Currency, PriceRange>,
    public readonly durationRange: DurationRange,
    public selected: boolean,
  ) {
    super();
  }

  get maxDuration(): Duration {
    return this.durationRange.to;
  }

  get $typeId(): string {
    return ServiceModifier.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(ServiceModifier.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ServiceModifier {
    return new ServiceModifier(
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'names') as SerializedData),
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'descriptions') as SerializedData),
        MapUtils.get<boolean>(map, 'isPricePublic'),
        toEnumFromString(MapUtils.get<PriceType>(map, 'priceType'), PriceType),
        MapUtils.getMap<Currency, PriceRange>(map, 'priceRanges', (k, v) => {
          return { key: <Currency>k, value: context.fromData<PriceRange>(v as SerializedData) };
        }),
        context.fromData<DurationRange>(MapUtils.getRaw(map, 'durationRange') as SerializedData),
        MapUtils.get<boolean>(map, 'selected'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'names': context.toData(this.names),
      'descriptions': context.toData(this.descriptions),
      'isPricePublic': this.isPricePublic,
      'priceType': fromEnumAsString(this.priceType),
      'priceRanges': mapToData(this.priceRanges, (k, v) => {
        return { key: k.toString(), value: context.toData(v) };
      }),
      'durationRange': toData(this.durationRange),
      'selected': this.selected,
    };
  }

  clone(): this {
    return new ServiceModifier(
        this.names.clone(),
        this.descriptions.clone(),
        this.isPricePublic,
        this.priceType,
        cloneMap(this.priceRanges),
        this.durationRange.clone(),
        this.selected,
    ) as this;
  }

  isSame(other: ServiceModifier): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.names, this.names) &&
      isSameData(other.descriptions, this.descriptions) &&
      other.isPricePublic == this.isPricePublic &&
      other.priceType == this.priceType &&
      isSameMap(other.priceRanges, this.priceRanges, isSameData) &&
      other.durationRange.isSame(this.durationRange) &&
      other.selected == this.selected;
  }
}

export class ServiceModifierBuilder extends DataModelBuilder<ServiceModifier> {
  build(context: SerializationContext, map: SerializedData): ServiceModifier {
    return ServiceModifier.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, 'priceType', PriceType, PriceType.PricePerService);

    if(versionRange.contains(ServiceModifier.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(ServiceModifier.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // / In this version `isPricePublic` was introduced.
    if(version < 2) {
      MapUtils.set(map, 'isPricePublic', true);
      version = 2;
    }

    // In this version `prices` of type [Map<Currency, Price>]
    // was changed to `priceRanges` of type [Map<Currency, PriceRange>].
    if(version < 3) {
      const oldPrices = MapUtils.getMap<Currency, Price>(map, 'prices', (k, v) => {
        return { key: <Currency>k, value: Price.decode(v as string) };
      });
      const newPrices = convertMapEntries<Currency, Price, Currency, PriceRange>(oldPrices, (k, v) => {
        return { key: k, value: PriceRange.exactly(v) };
      });

      const data = mapToData(newPrices, (k, v) => {
        return { key: k.toString(), value: context.toData(v) };
      });

      MapUtils.set(map, 'priceRanges', data);
      version = 3;
    }

    // / In this version `duration` of type [Duration] was changed to `durationRange` of type [DurationRange].
    if(version < 4) {
      const duration = context.decodeDuration(MapUtils.get<number>(map, 'duration'));

      MapUtils.set(map, 'durationRange', context.toData(DurationRange.exactly(duration)));
      version = 4;
    }

    // / In this version `priceType` of type [PriceType] was introduced.
    const priceType = toEnumFromString(MapUtils.get<PriceType>(map, 'priceType'), PriceType);
    if(version < 5 || !priceType) {
      MapUtils.set(map, 'priceType', PriceType.PricePerService);

      version = 5;
    }

    if(version < 6) {
      const name = MapUtils.get<string>(map, 'name');
      const description = MapUtils.get<string>(map, 'description');

      const language = context.storage.get<Language>('language', Language.English) ?? Language.English;
      const names = LocalizedString.forLanguage(language, name);
      const descriptions = LocalizedString.forLanguage(language, description);

      MapUtils.set(map, 'names', context.toData(names));
      MapUtils.set(map, 'descriptions', context.toData(descriptions));

      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
